<script setup lang="ts">
import i18next from "i18next";
import { useTranslation } from "i18next-vue";
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRotatingPhrases } from "#/hooks/useRotatingPhrases/useRotatingPhrases";
import { useCartStore } from "#/store/cart";
import { useMainStore } from "#/store/main";
import { useOrgUnitStore } from "#/store/org-unit.ts";
import { useThemeStore } from "#/store/theme";
import { getLocaleName } from "#/utils/i18next";
import KsoPrinter from "./ksoPrinter/KsoPrinter.vue";
import KsoTicTacToe from "./ksoTicTacToe/KsoTicTacToe.vue";

const { t } = useTranslation();
const themeStore = useThemeStore();
const storeCart = useCartStore();
const storeMain = useMainStore();
const orgUnit = useOrgUnitStore();

// @tolgee-key { key: 'kso.print.phrase.fiscal', ns: 'translation' }
// @tolgee-key { key: 'kso.print.phrase.paper', ns: 'translation' }
// @tolgee-key { key: 'kso.print.phrase.stamp', ns: 'translation' }
// @tolgee-key { key: 'kso.print.phrase.seal', ns: 'translation' }
// @tolgee-key { key: 'kso.print.phrase.almost', ns: 'translation' }
const PHRASE_KEYS = ["fiscal", "paper", "stamp", "seal", "almost"];

const loaderScreen = computed(() => themeStore.data?.loader_screen);

const phrases = computed(() => {
	const fromBackend = (loaderScreen.value?.phrase?.fiscalized ?? []).filter((item: string) => !!item?.trim());
	// @tolgee-ignore
	return fromBackend.length ? fromBackend : PHRASE_KEYS.map((key) => t(`kso.print.phrase.${key}`));
});

const { phrase, index } = useRotatingPhrases(
	phrases,
	() => loaderScreen.value?.phrase_interval,
	() => true,
);

const elapsed = ref(0);
let timer: ReturnType<typeof setInterval> | null = null;

const elapsedLabel = computed(() => {
	const minutes = Math.floor(elapsed.value / 60);
	const seconds = elapsed.value % 60;
	return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
});

onMounted(() => {
	timer = setInterval(() => {
		elapsed.value += 1;
	}, 1000);
});

onUnmounted(() => {
	if (timer) clearInterval(timer);
});

const readPersistedCart = () => {
	try {
		const raw = sessionStorage.getItem("_cart") || localStorage.getItem("cart");
		const parsed = raw ? JSON.parse(raw) : null;
		return Array.isArray(parsed) ? parsed : [];
	} catch {
		return [];
	}
};

const receiptItems = computed(() => {
	const persisted = readPersistedCart();
	const source = persisted.length ? persisted : storeMain.cartProducts_lib;
	return source.map((item: any) => ({
		name: getLocaleName(item, i18next.language) || item?.name || "",
		amount: item?.amount || 1,
		price: (Number(item?.price) || 0) * (item?.amount || 1),
	}));
});

const receiptDiscount = computed(() => storeCart.extraDiscount || 0);
const receiptTotal = computed(() => storeCart.cashPaymentAmount || null);
const currencySymbol = computed(() => storeMain.config_lib?.currency_symbol ?? null);
const receiptTitle = computed(() => orgUnit.data?.name || null);
</script>

<template>
	<div class="kso_print">
		<div class="kso_print_head">
			<h1 class="kso_print_title">{{ t(`kso.print.title`) }}</h1>
			<Transition name="kso_print_phrase" mode="out-in">
				<p :key="index" class="kso_print_phrase">{{ phrase }}</p>
			</Transition>
			<div class="kso_print_timer_row">
				<span class="kso_print_time">{{ elapsedLabel }}</span>
				<span class="kso_print_timer_caption">{{ t(`kso.print.timer.caption`) }}</span>
			</div>
		</div>

		<KsoTicTacToe class="kso_print_game" />

		<KsoPrinter
			class="kso_print_printer"
			:title="receiptTitle"
			:items="receiptItems"
			:discount="receiptDiscount"
			:total="receiptTotal"
			:currency-symbol="currencySymbol"
		/>
	</div>
</template>

<style lang="scss" scoped>
  @use './cartCompleteKsoPrint.scss';
</style>
